import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  UpsertEntityInput,
  useUpsertEntity,
} from 'src/data-capture/merge-review/useUpsertEntities';
import { useConnectAsset } from 'src/financial/financial-data/hooks/useConnectAsset';
import { FinancialCategory } from 'src/financial/financial-data/hooks/useFinancialItems';
import useFinancialEntityLayout from 'src/UI/dynamicUI/hooks/useFinancialEntityLayout';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { ApiError, ApiErrorCode } from 'src/error/ApiError';
import { Logger } from 'src/utils/Logger';
import { useFlags } from 'src/feature-flags/FlagsHook';
import {
  useFamilyMembers,
  type FamilyMember,
} from 'src/non-financial-data/v2/family/useFamilyMembers';
import {
  normaliseOwnersForPayload,
  productSupportsOwners,
  type OwnerRowValue,
} from 'src/financial/financial-data/financial-item/forms/common/OwnerSharesSection';
import {
  financialsReturnPath,
  RETURN_TO_PARAM,
} from 'src/financial/financial-data/financial-item/rework/returnPath';
import { AssetUpdateChoiceModal, ProviderReferenceModal } from './AssetCreationModals';
import { FamilyOwnerModal, type OwnerSelection } from './FamilyOwnerModal';

type PendingCreate = { category: FinancialCategory; product: string };

type UseCreateFinancialItemArgs = {
  clientId: string;
  onNavigate?: () => void;
  // The entity layout is needed to build a connected asset on submit. Callers
  // that let the user browse products before committing (the picker) pass the
  // current selection so the layout is prefetched rather than fetched only once
  // creation begins.
  prefetchEntityType?: string;
  // `useFamilyMembers` resolves the family from the current client, and the family route has
  // none. Callers already holding the roster pass it in, or the owner step would be skipped
  // there and every member added as an owner unasked.
  members?: FamilyMember[];
  // The item hangs off one client whoever added it, so a caller that is not the client's own
  // Financials says where saving should return to.
  returnTo?: string;
};

export function useCreateFinancialItem({
  clientId,
  onNavigate,
  prefetchEntityType,
  members: providedMembers,
  returnTo,
}: UseCreateFinancialItemArgs) {
  const navigate = useNavigate();
  const [pending, setPending] = useState<PendingCreate | null>(null);
  const [ownerModalOpened, setOwnerModalOpened] = useState(false);
  const [selectedOwners, setSelectedOwners] = useState<OwnerSelection[]>([]);
  const [choiceModalOpened, setChoiceModalOpened] = useState(false);
  const [providerModalOpened, setProviderModalOpened] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<'connection' | 'manually'>('connection');
  const [isUpserting, setIsUpserting] = useState(false);

  const {
    flagEnabledFamilyScreenRework,
    flagEnabledFinancialItemOwners,
    flagEnabledFinancialRework,
  } = useFlags();
  const { members: resolvedMembers, hasOtherMembers: resolvedHasOtherMembers } = useFamilyMembers({
    enabled:
      providedMembers === undefined &&
      flagEnabledFamilyScreenRework &&
      flagEnabledFinancialItemOwners,
  });
  const members = providedMembers ?? resolvedMembers;
  const hasOtherMembers =
    providedMembers === undefined
      ? resolvedHasOtherMembers
      : providedMembers.some(member => member.clientId !== clientId);
  const showOwnerStep =
    flagEnabledFinancialItemOwners && flagEnabledFamilyScreenRework && hasOtherMembers;

  const { upsertEntity } = useUpsertEntity();
  const { layout, isLoading: isLoadingLayout } = useFinancialEntityLayout({
    entityType: pending?.product ?? prefetchEntityType,
  });
  const { connectAsset } = useConnectAsset({ clientId });

  const navigateAndClose = (path: string) => {
    navigate(path);
    onNavigate?.();
  };

  const buildCreateUrl = (
    product: string,
    extra?: Record<string, string>,
    owners: OwnerSelection[] = selectedOwners,
  ) => {
    const params = new URLSearchParams(extra);
    if (returnTo) {
      params.set(RETURN_TO_PARAM, returnTo);
    }
    if (owners.length > 0) {
      params.set('owners', JSON.stringify(owners));
      const internalCount = owners.filter(owner => 'client_id' in owner).length;
      params.set('joint', internalCount >= 2 ? 'true' : 'false');
    }
    const query = params.toString();
    return `/client/${clientId}/financial-items/create/${encodeURIComponent(product)}${query ? `?${query}` : ''}`;
  };

  const proceedAfterOwners = (
    category: FinancialCategory,
    product: string,
    owners: OwnerSelection[],
  ) => {
    if (category === 'ASSET') {
      setChoiceModalOpened(true);
    } else {
      navigateAndClose(buildCreateUrl(product, undefined, owners));
    }
  };

  // `initialOwners` pre-ticks the owner picker for callers that already know the likely answer —
  // the family financial panel, where an item added from the family view usually belongs to the
  // whole family. The adviser still confirms, so in a family of three or more an item is not
  // silently owned by everyone. Two or more internal owners make it joint, via buildCreateUrl.
  const beginCreate = (
    category: FinancialCategory,
    product: string,
    initialOwners?: OwnerSelection[],
  ) => {
    setPending({ category, product });
    // Crypto, collectibles and pensions have no owner table, and their forms never render the
    // owners section, so an owners payload would be dropped on save. Carrying owners into one
    // would promise an ownership split the item cannot keep.
    const owners = initialOwners && productSupportsOwners(product) ? initialOwners : [];
    setSelectedOwners(owners);
    if (!showOwnerStep || !productSupportsOwners(product)) {
      proceedAfterOwners(category, product, owners);
    } else {
      setOwnerModalOpened(true);
    }
  };

  const handleOwnersContinue = (owners: OwnerSelection[]) => {
    setOwnerModalOpened(false);
    setSelectedOwners(owners);
    if (pending) {
      proceedAfterOwners(pending.category, pending.product, owners);
    }
  };

  const handleContinue = () => {
    setChoiceModalOpened(false);
    if (!pending) return;
    if (selectedMethod === 'manually') {
      navigateAndClose(buildCreateUrl(pending.product));
    } else {
      setProviderModalOpened(true);
    }
  };

  const handleProviderSubmit = async (identifiers: {
    providerReference?: string;
    policyNumber?: string;
  }) => {
    setProviderModalOpened(false);
    const product = pending?.product;
    const { providerReference, policyNumber } = identifiers;

    if (pending?.category === 'ASSET' && product && clientId && layout) {
      try {
        setIsUpserting(true);

        const metadata = layout.entitiesMetadata?.[product];
        if (!metadata) {
          throw new Error(`No metadata found for entity type: ${product}`);
        }

        const includeOwners = flagEnabledFinancialItemOwners && selectedOwners.length > 0;
        const entityValues = {
          ...(providerReference && { provider_reference_number: providerReference }),
          ...(policyNumber && { policy_number: policyNumber }),
          ...(includeOwners
            ? { owners: normaliseOwnersForPayload(selectedOwners as OwnerRowValue[]) }
            : {}),
        };

        const upsertInput: UpsertEntityInput = {
          entityFieldsMetadata: metadata,
          entityType: product,
          values: entityValues,
        };
        const upsertedEntityId = await upsertEntity(clientId, upsertInput, true);

        if (upsertedEntityId) {
          try {
            await connectAsset(upsertedEntityId);

            notifications.show({
              title: 'Success',
              message: 'Asset connection created and populated successfully',
              color: 'green',
            });
          } catch (connectError) {
            if (!ApiError.isApiError(ApiErrorCode.NO_UNPROCESSED_VALUATION_UPDATE, connectError)) {
              Logger.warn('Failed to connect asset to feed after creation', {
                connectError,
                assetId: upsertedEntityId,
              });
            }

            notifications.show({
              title: 'Partially successful',
              message:
                'Asset created successfully, but failed to connect to feed data. You can manually connect it later.',
              color: 'yellow',
            });
          }

          navigateAndClose(
            flagEnabledFinancialRework
              ? financialsReturnPath(clientId, returnTo)
              : `/client/${clientId}/financial-items/${encodeURIComponent(product)}/${encodeURIComponent(upsertedEntityId)}/`,
          );
        } else {
          throw new Error('Failed to create asset connection');
        }
      } catch (error) {
        notifyAndLogError(
          'Error creating asset connection',
          'Something went wrong trying to create the asset connection. Please try again and contact support if the issue persists.',
          error,
          { entityType: product, providerReference, policyNumber },
        );
      } finally {
        setIsUpserting(false);
      }
    } else {
      const extra: Record<string, string> = {};
      if (providerReference) extra.providerReference = providerReference;
      if (policyNumber) extra.policyNumber = policyNumber;
      navigateAndClose(buildCreateUrl(product ?? '', extra));
    }
  };

  const internalOwnerIds = selectedOwners.flatMap(owner =>
    'client_id' in owner ? [owner.client_id] : [],
  );
  const initialClientIds = internalOwnerIds.length > 0 ? internalOwnerIds : undefined;

  const modals = (
    <>
      <FamilyOwnerModal
        opened={ownerModalOpened}
        onClose={() => setOwnerModalOpened(false)}
        members={members}
        currentClientId={clientId}
        category={pending?.category}
        initialClientIds={initialClientIds}
        onContinue={handleOwnersContinue}
      />
      <AssetUpdateChoiceModal
        opened={choiceModalOpened}
        onClose={() => setChoiceModalOpened(false)}
        selectedMethod={selectedMethod}
        onMethodChange={setSelectedMethod}
        onContinue={handleContinue}
      />
      <ProviderReferenceModal
        opened={providerModalOpened}
        onClose={() => setProviderModalOpened(false)}
        onSubmit={handleProviderSubmit}
        isLoading={isUpserting || isLoadingLayout}
        clientId={clientId}
        product={pending?.product}
      />
    </>
  );

  return { beginCreate, modals };
}
